import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import NavLangStackEditor from './NavLangStackEditor';
import {
  itemTemplate, updateAt, removeAt, missingLangs,
} from './navEditorState';

// The top-level nav items as a horizontal pill bar, like the rendered menu.
// Click a pill to open its dropdown below; ✎ edits key/text/url/dropdown flag.
const NavItemsBar = ({
  items, selected, onSelect, onChange,
}) => {
  const [editing, setEditing] = useState(null);
  const current = editing !== null ? items[editing] : null;

  return (
    <div className="mnv-itemsbar">
      <span className="mnv-lab">Main nav — drag ⋮⋮ to reorder · click a pill to open it · ✎ to edit</span>
      <Droppable droppableId="items" direction="horizontal" type="ITEM">
        {provided => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="mnv-pills">
            {items.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Draggable key={index} draggableId={`item-${index}`} index={index}>
                {p => (
                  <div
                    ref={p.innerRef}
                    {...p.draggableProps}
                    className={`mnv-pill${selected === index ? ' mnv-pill--on' : ''}${item.visible === false ? ' mnv-dim' : ''}`}
                  >
                    <span className="mnv-grip" {...p.dragHandleProps}>⋮⋮</span>
                    <button type="button" className="mnv-pilllabel" onClick={() => onSelect(index)}>
                      {((item.text && item.text.en) || item.key || '(new)').toUpperCase()}
                      {item.hasDropdown && <span className="mnv-caret">▼</span>}
                      {missingLangs(item.text).length > 0 && <i className="mnv-dot" title="missing translations" />}
                    </button>
                    <button
                      type="button"
                      className="mnv-iconbtn"
                      title="Edit item"
                      onClick={() => setEditing(editing === index ? null : index)}
                    >
                      ✎
                    </button>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            <button
              type="button"
              className="mnv-add"
              onClick={() => {
                onChange([...items, itemTemplate()]);
                onSelect(items.length);
                setEditing(items.length);
              }}
            >
              + item
            </button>
          </div>
        )}
      </Droppable>
      {current && (
        <NavLangStackEditor
          label={`Item · ${(current.text && current.text.en) || current.key || 'new'}`}
          text={current.text}
          onChangeText={text => onChange(updateAt(items, editing, { ...current, text }))}
          url={current.hasDropdown ? '' : current.link}
          onChangeUrl={current.hasDropdown ? null : (link => onChange(updateAt(items, editing, { ...current, link })))}
          urlPlaceholder="/tours or https://…"
          visible={current.visible}
          onToggleVisible={() => onChange(updateAt(items, editing, { ...current, visible: current.visible === false }))}
          onDelete={() => { setEditing(null); onChange(removeAt(items, editing)); }}
          onDone={() => setEditing(null)}
        >
          <div className="mnv-langrow">
            <span className="mnv-lang mnv-lang--url">KEY</span>
            <input
              type="text"
              className="mnv-input mnv-input--mono"
              placeholder="slug, e.g. festivals"
              value={current.key || ''}
              onChange={e => onChange(updateAt(items, editing, { ...current, key: e.target.value }))}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label className="mnv-check">
              <input
                type="checkbox"
                checked={current.hasDropdown === true}
                onChange={() => onChange(updateAt(items, editing, { ...current, hasDropdown: current.hasDropdown !== true }))}
              />
              has dropdown
            </label>
          </div>
        </NavLangStackEditor>
      )}
    </div>
  );
};

NavItemsBar.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  selected: PropTypes.number.isRequired,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default NavItemsBar;
